import { Divider, Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ParameterSetDetailDto } from 'src/store/parametersets/parameterset';
import { selectParameterSetTypes } from 'src/store/parametersets/parametersets.selectors';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  parameterSet: Nullable<ParameterSetDetailDto>;
  onCancel: () => void;
  onSubmit: (parameterSet: ParameterSetDetailDto) => void;
};

export const ParameterSetDetailsForm: React.FC<Props> = props => {
  const { onSubmit, onCancel } = props;
  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);

  const [parameterSet, setParameterSet] = useState<ParameterSetDetailDto>(
    props.parameterSet ||
      ({
        private: false
      } as ParameterSetDetailDto)
  );

  const parameterSetTypes$ = useSelector(selectParameterSetTypes);

  const { t } = useTranslation();

  useEffect(() => {
    if (props.parameterSet) {
      setParameterSet(props.parameterSet);
    }
  }, [props.parameterSet]);

  const validate = (parameterSet: ParameterSetDetailDto) => {
    const validationRunners = [
      validationRunner('no', t('props.number'), required),
      validationRunner('name', t('props.name'), required, maxLength(50)),
      validationRunner('text', t('parameterSet.text'), maxLength(200)),
      validationRunner('parameterSetTypeId', t('props.type'), required)
    ];
    return runValidations(parameterSet, validationRunners);
  };

  return (
    <Formik
      initialValues={parameterSet}
      validate={validate}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
        submitCount
      }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container>
            <Grid item md={12} lg={12} className="flex align-center jc-end">
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('save.verb')}
                disabled={isSubmitting}
                onClick={() => {
                  setShowValidationErrors(true);
                }}
              />
            </Grid>
          </Grid>

          <Divider className="my-16" />

          <InputField
            name="no"
            label={t('props.number')}
            value={values.no}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.no}
            displayError={touched.no || submitCount > 0}
          />
          <InputField
            name="name"
            label={t('props.name')}
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.name}
            displayError={touched.name || submitCount > 0}
          />
          <InputField
            name="text"
            multiline
            rows={4}
            label={t('parameterSet.text')}
            value={values.text}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.text}
            displayError={touched.text || submitCount > 0}
          />
          <FormField>
            <Autocomplete
              options={parameterSetTypes$}
              label={t('props.type')}
              optionField="name"
              value={values.parameterSetType}
              onChange={parameterSetType => {
                setFieldValue('parameterSetTypeId', parameterSetType?.id);
              }}
              onBlur={handleBlur}
              errorMessage={errors.parameterSetTypeId}
              displayError={!!errors.parameterSetTypeId && showValidationErrors}
            />
          </FormField>
        </Form>
      )}
    </Formik>
  );
};
